// src/components/MapView.jsx
// Map view showing deal locations with filtering
// Multi-select filters for stages, states, and geographic regions

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polygon, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ArrowLeft, Filter, MapPin, Building2, LogOut, Loader2, Flame, ChevronDown, X, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useDisplayColors } from '../lib/ThemeContext';
import { pointInPolygon } from './TerritoryMapEditor';
import { titleCaseLocation } from '../lib/textFormat';
import 'leaflet/dist/leaflet.css';

// LFG Brand Colors
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Alfie: the map is too crowded showing Cancelled/On Hold/Prospect/Future
// Target deals by default — hide these stages unless the user explicitly
// picks stage chips (see filteredMarkers below).
const MAP_DEFAULT_HIDDEN_STAGES = new Set(['canceled', 'dead', 'on-hold', 'prospect', 'future-target']);

// Default center (US center)
const DEFAULT_CENTER = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;

// ============================================================================
// REGION DEFINITIONS
// ============================================================================
const REGIONS = [
  { id: 'toledo',       name: 'Toledo, OH',       lat: 41.6528,  lng: -83.5379, radiusMiles: 50, color: '#3b82f6' },
  { id: 'akron-canton', name: 'Akron/Canton, OH',  lat: 40.9987,  lng: -81.4428, radiusMiles: 30, color: '#8b5cf6' },
  { id: 'pittsburgh',   name: 'Pittsburgh, PA',    lat: 40.4406,  lng: -79.9959, radiusMiles: 25, color: '#ef4444' },
  { id: 'cleveland',    name: 'Cleveland, OH',     lat: 41.4993,  lng: -81.6944, radiusMiles: 30, color: '#f59e0b' },
];

// Convert miles to meters for Leaflet Circle
const milesToMeters = (miles) => miles * 1609.344;

// ============================================================================
// MULTI-SELECT DROPDOWN COMPONENT
// ============================================================================
function MultiSelectDropdown({ label, options, selected, onChange, colorMap, icon }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleOption = (value) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  };

  const selectAll = () => onChange(new Set(options.map(o => o.value)));
  const clearAll = () => onChange(new Set());

  const selectedCount = selected.size;
  const noneSelected = selectedCount === 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] rounded-lg transition-colors hover:bg-slate-50"
        style={{ backgroundColor: 'white', border: '1px solid #E5E9F0', color: '#334155' }}
      >
        {icon}
        <span className="truncate max-w-[140px] font-medium">
          {noneSelected
            ? `All ${label}`
            : selectedCount === 1
              ? options.find(o => selected.has(o.value))?.label || label
              : `${selectedCount} ${label}`}
        </span>
        {!noneSelected && (
          <span
            className="ml-0.5 transition-colors"
            style={{ color: '#94A3B8' }}
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
          >
            <X className="w-3 h-3" />
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: '#94A3B8' }} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1.5 rounded-xl overflow-hidden z-[1000] min-w-[220px] max-h-[340px] overflow-y-auto py-1"
          style={{
            backgroundColor: 'white',
            border: '1px solid #E5E9F0',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
          }}
        >
          <div className="flex items-center justify-between px-3 py-2 text-[11px]"
               style={{ borderBottom: '1px solid #F0F2F6', color: '#64748B' }}>
            <button onClick={selectAll} className="hover:underline font-medium">Select all</button>
            <button onClick={clearAll} className="hover:underline font-medium">Clear</button>
          </div>
          {options.map(opt => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer text-[13px] transition-colors hover:bg-[#FAFBFD]"
            >
              <input
                type="checkbox"
                checked={selected.has(opt.value)}
                onChange={() => toggleOption(opt.value)}
                className="rounded accent-[#1A3665]"
              />
              {colorMap && colorMap[opt.value] && (
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[opt.value] }} />
              )}
              <span className="truncate" style={{ color: '#0F172A' }}>{opt.label}</span>
              {opt.suffix && (
                <span className="text-[11px] ml-auto flex-shrink-0 tabular-nums" style={{ color: '#94A3B8' }}>{opt.suffix}</span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// REGION DROPDOWN with editable radii
// ============================================================================
function RegionDropdown({ selected, onChange, radii, onRadiiChange, colorMap, territories = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleRegion = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const selectAll = () => onChange(new Set([
    ...REGIONS.map(r => r.id),
    ...territories.map(t => `territory:${t.id}`),
  ]));
  const clearAll = () => onChange(new Set());

  const updateRadius = (id, value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0 && num <= 200) {
      onRadiiChange(prev => ({ ...prev, [id]: num }));
    }
  };

  const resetDefaults = () => {
    const defaults = {};
    REGIONS.forEach(r => { defaults[r.id] = r.radiusMiles; });
    onRadiiChange(defaults);
  };

  const selectedCount = selected.size;
  const noneSelected = selectedCount === 0;
  const labelForSelected = (id) => {
    if (id.startsWith('territory:')) {
      return territories.find(t => `territory:${t.id}` === id)?.name || 'Territory';
    }
    return REGIONS.find(r => r.id === id)?.name || 'Region';
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] rounded-lg transition-colors hover:bg-slate-50"
        style={{ backgroundColor: 'white', border: '1px solid #E5E9F0', color: '#334155' }}
      >
        <MapPin className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
        <span className="truncate max-w-[140px] font-medium">
          {noneSelected
            ? 'All Regions'
            : selectedCount === 1
              ? labelForSelected([...selected][0])
              : `${selectedCount} Regions`}
        </span>
        {!noneSelected && (
          <span
            className="ml-0.5"
            style={{ color: '#94A3B8' }}
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
          >
            <X className="w-3 h-3" />
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: '#94A3B8' }} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1.5 rounded-xl overflow-hidden z-[1000] min-w-[300px] py-1"
          style={{
            backgroundColor: 'white',
            border: '1px solid #E5E9F0',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
          }}
        >
          <div className="flex items-center justify-between px-3 py-2 text-[11px]"
               style={{ borderBottom: '1px solid #F0F2F6', color: '#64748B' }}>
            <div className="flex items-center gap-3">
              <button onClick={selectAll} className="hover:underline font-medium">Select all</button>
              <button onClick={clearAll} className="hover:underline font-medium">Clear</button>
            </div>
            <button onClick={resetDefaults} className="hover:underline font-medium">Reset radii</button>
          </div>
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
            Built-in radius regions
          </div>
          {REGIONS.map(region => (
            <div
              key={region.id}
              className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-[#FAFBFD] text-[13px]"
            >
              <input
                type="checkbox"
                checked={selected.has(region.id)}
                onChange={() => toggleRegion(region.id)}
                className="rounded cursor-pointer accent-[#1A3665]"
              />
              {colorMap && colorMap[region.id] && (
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[region.id] }} />
              )}
              <span className="truncate flex-1" style={{ color: '#0F172A' }}>{region.name}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={radii[region.id] || region.radiusMiles}
                  onChange={(e) => updateRadius(region.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-14 text-[11px] text-center rounded-md px-1 py-1 tabular-nums focus:outline-none"
                  style={{ border: '1px solid #E5E9F0', color: '#0F172A' }}
                />
                <span className="text-[10px]" style={{ color: '#94A3B8' }}>mi</span>
              </div>
            </div>
          ))}
          {territories.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider"
                   style={{ color: '#94A3B8', borderTop: '1px solid #F0F2F6', marginTop: 4 }}>
                Custom territories
              </div>
              {territories.map(t => {
                const id = `territory:${t.id}`;
                return (
                  <div
                    key={id}
                    className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-[#FAFBFD] text-[13px]"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(id)}
                      onChange={() => toggleRegion(id)}
                      className="rounded cursor-pointer accent-[#1A3665]"
                    />
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: t.color || '#7C3AED' }} />
                    <span className="truncate flex-1" style={{ color: '#0F172A' }}>{t.name}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MARKER ICON CREATORS
// ============================================================================
const createMarkerIcon = (color, isComplete = false, isCrematory = false) => {
  const completedOverlay = isComplete ? `
    <circle cx="12" cy="5" r="4" fill="#fff" stroke="${color}" stroke-width="1"/>
    <path d="M10 5 L11.5 6.5 L14 4" stroke="${color}" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  ` : '';

  if (isCrematory) {
    const svgIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="40">
        <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        <path d="M12 4C10 4 8.5 6 8.5 8.2c0 1.5.8 2.8 2 3.5-.6-.7-.8-1.4-.8-2.2 0-1.4 1.2-2.5 2.3-2.5 1.1 0 2.3 1.1 2.3 2.5 0 .8-.2 1.5-.8 2.2 1.2-.7 2-2 2-3.5C15.5 6 14 4 12 4z" fill="#fff" stroke="${color}" stroke-width="0.3"/>
        ${completedOverlay}
      </svg>
    `;
    return L.divIcon({
      html: svgIcon,
      className: 'custom-marker crematory-marker',
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [0, -40],
    });
  }

  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="40">
      <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5" fill="#fff"/>
      ${completedOverlay}
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  });
};

// US States for filter dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Create LFG-owned location marker (navy blue star, or navy+flame for crematory)
const createLfgMarkerIcon = (isCrematory = false) => {
  if (isCrematory) {
    // Crematory LFG: navy pin with large bright orange flame
    const svgIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="40">
        <path fill="${LFG_COLORS.navy}" stroke="#fff" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        <path d="M12 4C10 4 8.5 6 8.5 8.2c0 1.5.8 2.8 2 3.5-.6-.7-.8-1.4-.8-2.2 0-1.4 1.2-2.5 2.3-2.5 1.1 0 2.3 1.1 2.3 2.5 0 .8-.2 1.5-.8 2.2 1.2-.7 2-2 2-3.5C15.5 6 14 4 12 4z" fill="#f97316" stroke="#fff" stroke-width="0.5"/>
      </svg>
    `;
    return L.divIcon({
      html: svgIcon,
      className: 'custom-marker lfg-marker',
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [0, -40],
    });
  }
  // Regular LFG: navy pin with white star
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="36">
      <path fill="${LFG_COLORS.navy}" stroke="#fff" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <path d="M12 5.5l1.12 2.27 2.5.36-1.81 1.76.43 2.5L12 11.27l-2.24 1.12.43-2.5-1.81-1.76 2.5-.36z" fill="#fff"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker lfg-marker',
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -36],
  });
};

// Create a persistent distance label that floats above a marker like a tooltip
const createDistanceLabel = (distMiles) => {
  return L.divIcon({
    html: `<div style="
      position: relative;
      display: inline-block;
    "><div style="
      background: #1d4ed8;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 3px;
      white-space: nowrap;
      line-height: 13px;
      text-align: center;
    ">${distMiles}mi</div><div style="
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid #1d4ed8;
      margin: 0 auto;
    "></div></div>`,
    className: 'distance-label-marker',
    iconSize: [40, 20],
    iconAnchor: [20, 60],
  });
};

// Component to fit map bounds to markers
function FitBounds({ deals }) {
  const map = useMap();
  
  useEffect(() => {
    if (deals.length > 0) {
      const validDeals = deals.filter(d => d.latitude && d.longitude);
      if (validDeals.length > 0) {
        const bounds = L.latLngBounds(validDeals.map(d => [d.latitude, d.longitude]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      }
    }
  }, [deals, map]);
  
  return null;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function MapView({ 
  deals, 
  stages = [],
  organizationId,
  onSelectDeal,
  onBack,
  pc = LFG_COLORS.navy,
  sc = LFG_COLORS.gray,
  signOut
}) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
  const [selectedStages, setSelectedStages] = useState(new Set());
  const [selectedStates, setSelectedStates] = useState(new Set());
  // Phase R.2.8 — priority filter (same multi-select chip pattern as stages).
  // Markers without a priority (null) are kept visible regardless so the
  // user doesn't have to set a priority on every deal to see it on the map.
  const [selectedPriorities, setSelectedPriorities] = useState(new Set());
  const [selectedRegions, setSelectedRegions] = useState(new Set());
  const [regionRadii, setRegionRadii] = useState(() => {
    const defaults = {};
    REGIONS.forEach(r => { defaults[r.id] = r.radiusMiles; });
    return defaults;
  });
  const [showCrematoriesOnly, setShowCrematoriesOnly] = useState(false);
  const [showTerritories, setShowTerritories] = useState(true);
  const [locations, setLocations] = useState([]);
  const [lfgLocations, setLfgLocations] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Fetch all deal_locations AND lfg_locations AND territories for the organization
  useEffect(() => {
    async function fetchLocations() {
      if (!organizationId) return;
      try {
        const [dealResult, lfgResult, terResult] = await Promise.all([
          supabase
            .from('deal_locations')
            .select('*, deals(name, stage)')
            .eq('organization_id', organizationId)
            // Phase C.6 — AI-proposed locations stay off the map until a
            // human confirms them in DealView.
            .eq('is_confirmed', true),
          supabase
            .from('lfg_locations')
            .select('*')
            .eq('organization_id', organizationId)
            .eq('is_active', true),
          supabase
            .from('territories')
            .select('id, name, color, drawn_geometry, county_fips, states, is_active')
            .eq('organization_id', organizationId)
            .eq('is_active', true)
        ]);

        if (dealResult.error) throw dealResult.error;
        if (lfgResult.error) throw lfgResult.error;
        if (terResult.error) throw terResult.error;
        setLocations((dealResult.data || []).map(titleCaseLocation));
        setLfgLocations(lfgResult.data || []);
        setTerritories(terResult.data || []);
      } catch (err) {
        console.error('Error fetching locations:', err);
      } finally {
        setLoadingLocations(false);
      }
    }
    fetchLocations();
  }, [organizationId]);

  // Haversine distance (miles)
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Build a set of deal IDs that are actually LFG companies (complete deals whose locations match LFG locations)
  const lfgDealIds = useMemo(() => {
    const ids = new Set();
    if (!lfgLocations.length) return ids;
    deals.forEach(deal => {
      if (deal.stage === 'complete' || deal.stage === 'closed') {
        const dealLocs = locations.filter(l => l.deal_id === deal.id);
        const isLfg = dealLocs.some(dl => 
          lfgLocations.some(lfg => 
            lfg.name === dl.name && 
            (lfg.address || '') === (dl.address || '') &&
            (lfg.city || '') === (dl.city || '')
          )
        );
        if (isLfg) ids.add(deal.id);
      }
    });
    return ids;
  }, [deals, locations, lfgLocations]);

  // Combine deal addresses and separate locations into markers (excluding LFG-owned deals)
  const allMarkers = useMemo(() => {
    const markers = [];
    
    deals.forEach(deal => {
      // Skip deals that are LFG companies — they'll show as LFG markers instead
      if (lfgDealIds.has(deal.id)) return;
      
      const dealLocations = locations.filter(l => l.deal_id === deal.id);
      
      if (dealLocations.length > 0) {
        dealLocations.forEach(loc => {
          if (loc.latitude && loc.longitude) {
            // Compute nearest LFG distance
            let nearestLfgDist = null;
            const lfgWithCoords = lfgLocations.filter(l => l.latitude && l.longitude);
            if (lfgWithCoords.length) {
              let minD = Infinity;
              lfgWithCoords.forEach(lfg => {
                const d = haversineDistance(loc.latitude, loc.longitude, lfg.latitude, lfg.longitude);
                if (d < minD) minD = d;
              });
              if (minD < Infinity) nearestLfgDist = Math.round(minD);
            }

            markers.push({
              id: `loc-${loc.id}`,
              type: 'location',
              dealId: deal.id,
              dealName: deal.name,
              dealStage: deal.stage,
              dealPriority: deal.priority || null,
              locationName: loc.name,
              address: loc.address,
              city: loc.city,
              state: loc.state,
              zip: loc.zip,
              latitude: loc.latitude,
              longitude: loc.longitude,
              isCrematory: loc.is_crematory,
              nearestLfgDist
            });
          }
        });
      } else if (deal.latitude && deal.longitude) {
        let nearestLfgDist = null;
        const lfgWithCoords = lfgLocations.filter(l => l.latitude && l.longitude);
        if (lfgWithCoords.length) {
          let minD = Infinity;
          lfgWithCoords.forEach(lfg => {
            const d = haversineDistance(deal.latitude, deal.longitude, lfg.latitude, lfg.longitude);
            if (d < minD) minD = d;
          });
          if (minD < Infinity) nearestLfgDist = Math.round(minD);
        }

        markers.push({
          id: `deal-${deal.id}`,
          type: 'deal',
          dealId: deal.id,
          dealName: deal.name,
          dealStage: deal.stage,
          dealPriority: deal.priority || null,
          locationName: null,
          address: deal.address,
          city: deal.city,
          state: deal.state,
          zip: deal.zip,
          latitude: deal.latitude,
          longitude: deal.longitude,
          isCrematory: false,
          nearestLfgDist
        });
      }
    });
    
    return markers;
  }, [deals, locations, lfgLocations, lfgDealIds]);

  // LFG location markers (distinct style)
  const lfgMarkers = useMemo(() => {
    return lfgLocations
      .filter(l => l.latitude && l.longitude)
      .map(l => {
        // Check is_crematory from the lfg_locations table first,
        // then fall back to matching deal_location's is_crematory,
        // then fall back to checking notes for "(Crematory)" text
        let isCrematory = l.is_crematory || false;
        if (!isCrematory) {
          const matchingDealLoc = locations.find(dl =>
            dl.name === l.name &&
            (dl.address || '') === (l.address || '') &&
            (dl.city || '') === (l.city || '')
          );
          if (matchingDealLoc) isCrematory = matchingDealLoc.is_crematory || false;
        }
        if (!isCrematory && l.notes && l.notes.includes('(Crematory)')) {
          isCrematory = true;
        }
        return {
          id: `lfg-${l.id}`,
          type: 'lfg',
          name: l.name,
          address: l.address,
          city: l.city,
          state: l.state,
          zip: l.zip,
          latitude: l.latitude,
          longitude: l.longitude,
          isCrematory,
        };
      });
  }, [lfgLocations, locations]);
  
  // Get unique states from all markers
  const markerStates = useMemo(() => {
    const states = [...new Set(allMarkers.map(m => m.state).filter(Boolean))];
    return states.sort();
  }, [allMarkers]);

  // Check if a marker is inside any selected region (REGIONS or territories).
  const isMarkerInSelectedRegions = (marker) => {
    if (selectedRegions.size === 0) return true;
    for (const regionId of selectedRegions) {
      if (regionId.startsWith('territory:')) {
        const tid = regionId.slice('territory:'.length);
        const t = territories.find(x => x.id === tid);
        if (!t) continue;
        // 1) Drawn polygon — point-in-polygon test (preferred when present)
        const geom = t.drawn_geometry;
        if (geom?.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
          if (pointInPolygon(marker.latitude, marker.longitude, geom)) return true;
          continue;
        }
        // 2) Fall back to county_fips set
        if (Array.isArray(t.county_fips) && t.county_fips.length > 0 && marker.county_fips) {
          if (t.county_fips.includes(marker.county_fips)) return true;
          continue;
        }
        // 3) Fall back to states list
        if (Array.isArray(t.states) && t.states.length > 0 && marker.state) {
          if (t.states.includes(marker.state)) return true;
        }
      } else {
        const region = REGIONS.find(r => r.id === regionId);
        if (!region) continue;
        const dist = haversineDistance(marker.latitude, marker.longitude, region.lat, region.lng);
        if (dist <= (regionRadii[region.id] || region.radiusMiles)) return true;
      }
    }
    return false;
  };
  
  // Filter deal-side markers
  const filteredMarkers = useMemo(() => {
    return allMarkers.filter(m => {
      if (selectedStages.size > 0) {
        if (!selectedStages.has(m.dealStage)) return false;
      } else if (MAP_DEFAULT_HIDDEN_STAGES.has(m.dealStage)) {
        // No explicit stage selection — default-hide Cancelled/On Hold/
        // Prospect/Future Target so the map isn't crowded with inactive
        // deals. Picking any stage chip overrides this entirely.
        return false;
      }
      if (selectedPriorities.size > 0 && !selectedPriorities.has(m.dealPriority || 'unset')) return false;
      if (selectedStates.size > 0 && !selectedStates.has(m.state)) return false;
      if (!isMarkerInSelectedRegions(m)) return false;
      if (showCrematoriesOnly && !m.isCrematory) return false;
      return true;
    });
  }, [allMarkers, selectedStages, selectedPriorities, selectedStates, selectedRegions, regionRadii, showCrematoriesOnly, territories]);

  // Filter LFG markers — same region/state/crematory rules apply. (Stage
  // filter doesn't make sense for LFG-owned locations.) Without this the
  // crematory toggle left every LFG star on the map regardless.
  const filteredLfgMarkers = useMemo(() => {
    return lfgMarkers.filter(m => {
      if (selectedStates.size > 0 && !selectedStates.has(m.state)) return false;
      if (!isMarkerInSelectedRegions(m)) return false;
      if (showCrematoriesOnly && !m.isCrematory) return false;
      return true;
    });
  }, [lfgMarkers, selectedStates, selectedRegions, regionRadii, showCrematoriesOnly, territories]);
  
  // Deals that have no markers on the map at all
  const dealsWithoutMapPresence = useMemo(() => {
    const dealsOnMap = new Set(allMarkers.map(m => m.dealId));
    // Exclude LFG-owned deals — they're not "missing", they show as LFG markers
    return deals.filter(d => !dealsOnMap.has(d.id) && !lfgDealIds.has(d.id));
  }, [deals, allMarkers, lfgDealIds]);
  
  // Count crematories (deal + LFG)
  const crematoryCount = useMemo(() => {
    return allMarkers.filter(m => m.isCrematory).length + lfgMarkers.filter(m => m.isCrematory).length;
  }, [allMarkers, lfgMarkers]);
  
  // Get stage color
  const getStageColor = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.color || '#6b7280';
  };
  
  // Get stage name
  const getStageName = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.name || stageId;
  };
  
  // Check if stage is terminal/complete
  const isStageComplete = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.is_terminal || stageId === 'complete' || stageId === 'closed';
  };

  // Build color map for stage filter pills
  const stageColorMap = useMemo(() => {
    const map = {};
    stages.forEach(s => { map[s.id] = s.color || '#6b7280'; });
    return map;
  }, [stages]);

  // Build color map for region filter pills
  const regionColorMap = useMemo(() => {
    const map = {};
    REGIONS.forEach(r => { map[r.id] = r.color; });
    return map;
  }, []);

  // Stage options for multi-select
  const stageOptions = useMemo(() =>
    stages.map(s => ({ value: s.id, label: s.name })),
    [stages]
  );

  // State options for multi-select
  const stateOptions = useMemo(() =>
    markerStates.map(s => ({ value: s, label: s })),
    [markerStates]
  );

  // Phase R.2.8 — Priority options + color map. Order matches the
  // heat-map intent (Critical first). "Unset" is included so users can
  // filter on/off the deals that haven't been triaged yet.
  const priorityOptions = useMemo(() => [
    { value: 'critical', label: 'Critical' },
    { value: 'high',     label: 'High' },
    { value: 'medium',   label: 'Medium' },
    { value: 'low',      label: 'Low' },
    { value: 'unset',    label: 'No priority' },
  ], []);

  const priorityColorMap = useMemo(() => ({
    critical: '#DC2626',
    high:     '#F97316',
    medium:   '#EAB308',
    low:      '#CBD5E1',
    unset:    '#94A3B8',
  }), []);

  // (Region options are rendered inline in RegionDropdown with editable radii)

  // Active filter count for the summary
  const activeFilterCount = (selectedStages.size > 0 ? 1 : 0)
    + (selectedPriorities.size > 0 ? 1 : 0)
    + (selectedStates.size > 0 ? 1 : 0)
    + (selectedRegions.size > 0 ? 1 : 0)
    + (showCrematoriesOnly ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12 flex flex-col" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* Page heading */}
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Map
          </h1>
          <p className="text-sm mt-1 tabular-nums" style={{ color: '#475569' }}>
            {filteredMarkers.length} deal{filteredMarkers.length !== 1 ? 's' : ''} · {lfgMarkers.length} LFG location{lfgMarkers.length !== 1 ? 's' : ''}
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setSelectedStages(new Set());
                  setSelectedPriorities(new Set());
                  setSelectedStates(new Set());
                  setSelectedRegions(new Set());
                  setShowCrematoriesOnly(false);
                }}
                className="ml-2 text-xs hover:underline"
                style={{ color: '#1A3665' }}
              >
                Clear filters
              </button>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <MultiSelectDropdown
            label="Stages"
            options={stageOptions}
            selected={selectedStages}
            onChange={setSelectedStages}
            colorMap={stageColorMap}
            icon={<Filter className="w-3.5 h-3.5 text-gray-400" />}
          />
          <MultiSelectDropdown
            label="Priority"
            options={priorityOptions}
            selected={selectedPriorities}
            onChange={setSelectedPriorities}
            colorMap={priorityColorMap}
            icon={<Filter className="w-3.5 h-3.5 text-gray-400" />}
          />
          <MultiSelectDropdown
            label="States"
            options={stateOptions}
            selected={selectedStates}
            onChange={setSelectedStates}
            icon={<Building2 className="w-3.5 h-3.5 text-gray-400" />}
          />
          <RegionDropdown
            selected={selectedRegions}
            onChange={setSelectedRegions}
            radii={regionRadii}
            onRadiiChange={setRegionRadii}
            colorMap={regionColorMap}
            territories={territories}
          />
          {territories.length > 0 && (
            <button
              type="button"
              onClick={() => setShowTerritories(s => !s)}
              className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] rounded-lg transition-colors hover:bg-slate-50"
              style={{
                backgroundColor: 'white',
                border: '1px solid #E5E9F0',
                color: showTerritories ? '#7C3AED' : '#94A3B8',
              }}
              title={showTerritories ? 'Hide custom territory polygons' : 'Show custom territory polygons'}
            >
              {showTerritories ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              <span className="font-medium">Territories</span>
            </button>
          )}
          {crematoryCount > 0 && (
            <label
              className="h-9 inline-flex items-center gap-1.5 px-3 text-[13px] cursor-pointer rounded-lg transition-colors hover:bg-slate-50"
              style={{ border: '1px solid #E5E9F0', color: '#334155', backgroundColor: 'white' }}
            >
              <input
                type="checkbox"
                checked={showCrematoriesOnly}
                onChange={e => setShowCrematoriesOnly(e.target.checked)}
                className="rounded accent-orange-500"
              />
              <Flame className="w-3.5 h-3.5 text-orange-500" />
              <span>Crematories <span className="tabular-nums" style={{ color: '#94A3B8' }}>({crematoryCount})</span></span>
            </label>
          )}
        </div>
      </div>

      {/* Map card */}
      <div
        className="flex-1 rounded-2xl overflow-hidden flex flex-col min-h-0"
        style={{
          backgroundColor: '#ffffff',
          border: '1px solid #E5E9F0',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
        }}
      >
      <div className="flex-1 relative" style={{ minHeight: 480 }}>
        {loadingLocations ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: displayPrimary }} />
          </div>
        ) : (
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            className="h-full w-full"
            style={{ background: '#e5e7eb', height: '100%', minHeight: 480 }}
          >
            {/* OpenStreetMap standard tiles. CARTO's free basemap began
                requiring an API key in Sep 2026 ("API KEY REQUIRED" watermark);
                OSM needs no key. Busier than Voyager but fully functional. */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              subdomains="abc"
              maxZoom={19}
            />
            
            <FitBounds deals={[...filteredMarkers, ...filteredLfgMarkers]} />

            {/* Custom territory polygons (user-drawn shapes) — gated on toggle */}
            {showTerritories && territories.map(t => {
              const geom = t.drawn_geometry;
              if (!geom || geom.type !== 'Polygon' || !Array.isArray(geom.coordinates?.[0])) return null;
              // GeoJSON polygons are [lng, lat]; Leaflet wants [lat, lng]
              const positions = geom.coordinates[0].map(([lng, lat]) => [lat, lng]);
              const color = t.color || '#7C3AED';
              return (
                <Polygon
                  key={`territory-${t.id}`}
                  positions={positions}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.10,
                    weight: 2,
                    dashArray: '4 4',
                  }}
                >
                  <Tooltip sticky>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs" style={{ color: '#64748B' }}>
                      {(t.county_fips || []).length} counties
                    </div>
                  </Tooltip>
                </Polygon>
              );
            })}

            {/* Region circles */}
            {[...selectedRegions].map(regionId => {
              const region = REGIONS.find(r => r.id === regionId);
              if (!region) return null;
              return (
                <Circle
                  key={`region-${region.id}`}
                  center={[region.lat, region.lng]}
                  radius={milesToMeters(regionRadii[region.id] || region.radiusMiles)}
                  pathOptions={{
                    color: region.color,
                    fillColor: region.color,
                    fillOpacity: 0.08,
                    weight: 2,
                    dashArray: '6 4',
                  }}
                >
                  <Popup>
                    <div className="text-sm font-medium">{region.name}</div>
                    <div className="text-xs text-gray-500">{regionRadii[region.id] || region.radiusMiles}-mile radius</div>
                  </Popup>
                </Circle>
              );
            })}
            
            {/* LFG Location markers (navy star) */}
            {filteredLfgMarkers.map(marker => (
              <Marker
                key={marker.id}
                position={[marker.latitude, marker.longitude]}
                icon={createLfgMarkerIcon(marker.isCrematory)}
              >
                <Popup>
                  <div className="min-w-[220px]">
                    <div
                      className="text-[10px] font-semibold uppercase mb-1"
                      style={{ letterSpacing: '0.08em', color: '#64748B' }}
                    >
                      LFG Location
                    </div>
                    <div className="flex items-center gap-2 font-semibold text-[14px]" style={{ color: '#0F172A' }}>
                      {marker.isCrematory && <Flame className="w-4 h-4 flex-shrink-0" style={{ color: '#F97316' }} />}
                      <span>{marker.name}</span>
                    </div>
                    <div className="text-[12px] mt-1 leading-snug" style={{ color: '#64748B' }}>
                      {marker.address}<br />
                      {marker.city}, {marker.state} {marker.zip}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md font-medium text-white"
                        style={{ backgroundColor: LFG_COLORS.navy }}
                      >
                        ★ LFG
                      </span>
                      {marker.isCrematory && (
                        <span
                          className="text-[11px] px-2 py-0.5 rounded-md font-medium"
                          style={{ backgroundColor: '#FFEDD5', color: '#C2410C' }}
                        >
                          Crematory
                        </span>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Deal markers */}
            {filteredMarkers.map(marker => (
              <Marker
                key={marker.id}
                position={[marker.latitude, marker.longitude]}
                icon={createMarkerIcon(
                  getStageColor(marker.dealStage),
                  isStageComplete(marker.dealStage),
                  marker.isCrematory
                )}
              >
                <Popup>
                  <div className="min-w-[220px]">
                    <div
                      className="text-[10px] font-semibold uppercase mb-1"
                      style={{ letterSpacing: '0.08em', color: '#64748B' }}
                    >
                      Deal
                    </div>
                    <div className="flex items-center gap-2 font-semibold text-[14px]" style={{ color: '#0F172A' }}>
                      {marker.isCrematory && <Flame className="w-4 h-4 flex-shrink-0" style={{ color: '#F97316' }} />}
                      <span>{marker.dealName}</span>
                    </div>
                    {marker.locationName && (
                      <div className="text-[12px] mt-0.5" style={{ color: '#475569' }}>{marker.locationName}</div>
                    )}
                    <div className="text-[12px] mt-1 leading-snug" style={{ color: '#64748B' }}>
                      {marker.address}<br />
                      {marker.city}, {marker.state} {marker.zip}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                      <span
                        className="text-[11px] px-2 py-0.5 rounded-md font-medium text-white"
                        style={{ backgroundColor: getStageColor(marker.dealStage) }}
                      >
                        {getStageName(marker.dealStage)}
                      </span>
                      {marker.isCrematory && (
                        <span
                          className="text-[11px] px-2 py-0.5 rounded-md font-medium"
                          style={{ backgroundColor: '#FFEDD5', color: '#C2410C' }}
                        >
                          Crematory
                        </span>
                      )}
                      {marker.nearestLfgDist !== null && (
                        <span
                          className="text-[11px] px-2 py-0.5 rounded-md font-medium tabular-nums"
                          style={{ backgroundColor: '#EFF4FB', color: '#1A3665' }}
                        >
                          {marker.nearestLfgDist} mi to LFG
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => onSelectDeal(marker.dealId)}
                      className="mt-3 text-[12px] font-semibold hover:underline"
                      style={{ color: '#1A3665' }}
                    >
                      View Deal →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Distance labels — persistent badges next to deal markers */}
            {filteredMarkers.filter(m => m.nearestLfgDist !== null).map(marker => (
              <Marker
                key={`dist-${marker.id}`}
                position={[marker.latitude, marker.longitude]}
                icon={createDistanceLabel(marker.nearestLfgDist)}
                interactive={false}
                zIndexOffset={-1000}
              />
            ))}
          </MapContainer>
        )}
        
        {/* Legend */}
        <div
          className="absolute bottom-4 left-4 rounded-2xl p-3.5 z-[1000] backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid #E5E9F0',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
          }}
        >
          <h4
            className="text-[10px] font-semibold uppercase mb-2"
            style={{ letterSpacing: '0.08em', color: '#64748B' }}
          >
            Legend
          </h4>
          <div className="space-y-1.5">
            {stages.slice(0, 6).map(stage => (
              <div key={stage.id} className="flex items-center gap-2 text-[12px]" style={{ color: '#0F172A' }}>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                <span>{stage.name}</span>
                {stage.is_terminal && (
                  <span className="text-[10px]" style={{ color: '#16A34A' }}>✓</span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2.5 pt-2.5 space-y-1.5" style={{ borderTop: '1px solid #F0F2F6' }}>
            <div className="flex items-center gap-2 text-[12px]" style={{ color: '#0F172A' }}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: LFG_COLORS.navy }} />
              <span>★ LFG Location</span>
            </div>
            <div className="flex items-center gap-2 text-[12px]" style={{ color: '#0F172A' }}>
              <Flame className="w-3 h-3 flex-shrink-0" style={{ color: '#F97316' }} />
              <span>Crematory</span>
            </div>
          </div>
          {selectedRegions.size > 0 && (
            <div className="mt-2.5 pt-2.5 space-y-1.5" style={{ borderTop: '1px solid #F0F2F6' }}>
              <div
                className="text-[10px] font-semibold uppercase mb-1"
                style={{ letterSpacing: '0.08em', color: '#64748B' }}
              >
                Regions
              </div>
              {[...selectedRegions].map(regionId => {
                const region = REGIONS.find(r => r.id === regionId);
                if (!region) return null;
                return (
                  <div key={region.id} className="flex items-center gap-2 text-[12px]" style={{ color: '#0F172A' }}>
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ border: `2px solid ${region.color}`, backgroundColor: `${region.color}22` }}
                    />
                    <span className="flex-1">{region.name}</span>
                    <span className="tabular-nums text-[11px]" style={{ color: '#94A3B8' }}>
                      {regionRadii[region.id] || region.radiusMiles}mi
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Deals without any map presence — surface names so user can click
            into the offender and fix the address. */}
        {dealsWithoutMapPresence.length > 0 && (
          <div
            className="absolute top-4 right-4 rounded-2xl p-3.5 z-[1000] backdrop-blur-sm"
            style={{
              backgroundColor: 'rgba(254, 252, 232, 0.96)',
              border: '1px solid #FDE68A',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
              maxWidth: 280,
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            <h4
              className="text-[10px] font-semibold uppercase mb-1.5"
              style={{ letterSpacing: '0.08em', color: '#92400E' }}
            >
              {dealsWithoutMapPresence.length} deal{dealsWithoutMapPresence.length > 1 ? 's' : ''} not shown
            </h4>
            <p className="text-[11px] leading-snug mb-2" style={{ color: '#A16207' }}>
              No usable address. Click a deal to fix.
            </p>
            <div className="space-y-1">
              {dealsWithoutMapPresence.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onSelectDeal && onSelectDeal(d.id)}
                  className="w-full text-left text-[12px] px-2 py-1.5 rounded hover:bg-amber-100/60 transition-colors"
                  style={{ color: '#0F172A', border: '1px solid rgba(251, 191, 36, 0.3)', backgroundColor: 'rgba(255,255,255,0.5)' }}
                  title="Open this deal to add a location with a proper address"
                >
                  {d.name || '(unnamed)'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>{/* end map card */}
    </div>
  );
}
